import React, { useState, useEffect } from 'react';

// Material UI
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';

// Material UI Icons
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// Componentes 
import SeleccionarServicios from './SeleccionarServicios';

// Funciones para obtener datos
import ObtenerPlanes from '../functions/ObtenerPlanes';
import GuardarPlan from '../functions/GuardarPlan';
import InsertarPlan from '../functions/InsertarPlan';
import ObtenerServiciosPlanes from '../functions/ObtenerServiciosPlanes';
import GuardarServiciosPlan from '../functions/GuardarServiciosPlan';

// Funciones Helper
import { FechaHoraPostgreSQLTZ } from '../functions/FechaHoraPostgreSQLTZ';

// Tipos de datos
import PlanEditar from '../types/PlanEditar';
import PlanServiciosSeleccionar from '../types/PlanServiciosSeleccionar';
import ServiciosSeleccionar from '../types/ServiciosSeleccionar';
import ServiciosAPI from '../types/ServiciosAPI';

// Indicador de procesamiento
import CircularProgress from '@mui/material/CircularProgress';

interface EditarPlanProps {
    idPlan: number;
    guardarCambios: (plan: PlanEditar) => void;
    cancelarCambios: () => void;
    eliminandoPlan: boolean;
}

export const EditarPlan: React.FC<EditarPlanProps> = ({ idPlan, guardarCambios, cancelarCambios, eliminandoPlan = false }) => {
  
    const [isLoading, setIsLoading] = useState(true);
    const [plan, setPlan] = useState<PlanEditar | null>(null);
    const [errorPlan, setErrorPlan] = useState<string | null>(null);
    const [planServicios, setPlanServicios] = useState<PlanServiciosSeleccionar>({id: 0, title:'', servicios: []});

    const [localTitle, setLocalTitle] = useState('');
    const [localPrice, setLocalPrice] = useState('');
    const [localPriceDebito, setLocalPriceDebito] = useState('');
    const [localDescription, setLocalDescription] = useState('');
    const [localMaxConyuge, setLocalMaxConyuge] = useState('');
    const [localMaxMenores, setLocalMaxMenores] = useState('');
    const [localMaxMayores, setLocalMaxMayores] = useState('');
    const [localHeaderColor, setLocalHeaderColor] = useState('');
    const [localHeaderColorHover, setLocalHeaderColorHover] = useState('');
    const [localPriceAnual, setLocalPriceAnual] = useState('');
    const [localMaxEdadTitular, setLocalMaxEdadTitular] = useState('');
    const [localMaxEdadConyuge, setLocalMaxEdadConyuge] = useState('');
    const [localMaxEdadMenor, setLocalMaxEdadMenor] = useState('');
    const [localMaxEdadMayor, setLocalMaxEdadMayor] = useState('');
    const [localMostrar, setLocalMostrar] = useState('');
    const [localSolCelu, setLocalSolCelu] = useState('');
    const [localValCelu, setLocalValCelu] = useState('');
    const [localSolEmail, setLocalSolEmail] = useState('');
    const [localValEmail, setLocalValEmail] = useState('');
    const [localPagoOnline, setLocalPagoOnline] = useState('');
    const [localFchCrea, setLocalFchCrea] = useState('');
    const [localFchModi, setLocalFchModi] = useState('');
    const [localFchBaja, setLocalFchBaja] = useState('');
    const [localHabilitado, setLocalHabilitado] = useState('');
    const [localDadoBaja, setLocalDadoBaja] = useState('');
    const [localCodigoPlan, setLocalCodigoPlan] = useState('');
    const [serviciosPlanes, setServiciosPlanes] = useState<ServiciosSeleccionar[]>([]);

    // Método para ordenar los servicios antes de guardarlas en el arreglo 
    const ordenarServicios = (servicios: ServiciosSeleccionar[]) => {
        return servicios.sort((a, b) => {
            if (a.nombre < b.nombre) {
                return -1;
            }
            if (a.nombre > b.nombre) {
                return 1;
            }
            return 0;
        });
    };


    useEffect(() => {
        
        const fetchPlan = async () => {
            try {
                const data = await ObtenerPlanes('', idPlan);
                if (data.planes_ok === 'si') {

                    // Todo ok, plan recuperado
                    const planRecuperado: PlanEditar = {
                        id: data.planes[0].id,
                        headercolor: data.planes[0].headercolor,
                        headercolorhover: data.planes[0].headercolorhover,
                        title: data.planes[0].title,
                        price: data.planes[0].price,
                        pricedebito: data.planes[0].pricedebito,
                        priceanual: data.planes[0].priceanual,
                        description: data.planes[0].description,
                        maxconyuge: data.planes[0].maxconyuge,
                        maxmenores: data.planes[0].maxmenores,
                        maxmayores: data.planes[0].maxmayores,
                        habilitado: data.planes[0].habilitado,
                        fecha_creacion: data.planes[0].fecha_creacion,
                        fecha_modificacion: data.planes[0].fecha_modificacion,
                        dado_de_baja: data.planes[0].dado_de_baja,
                        fecha_baja: data.planes[0].fecha_baja,
                        maxedadtitular: data.planes[0].maxedadtitular,
                        maxedadconyuge: data.planes[0].maxedadconyuge,
                        maxedadmenor: data.planes[0].maxedadmenor,
                        maxedadmayor: data.planes[0].maxedadmayor,
                        codigoplan: data.planes[0].codigoplan,
                        mostrar: data.planes[0].mostrar,
                        sol_celu: data.planes[0].sol_celu,
                        val_celu: data.planes[0].val_celu,
                        sol_email: data.planes[0].sol_email,
                        val_email: data.planes[0].val_email,
                        pago_online: data.planes[0].pago_online,
                    };

                    setPlan(planRecuperado);
                    setLocalTitle(data.planes[0].title);
                    setLocalPrice(data.planes[0].price);
                    setLocalPriceDebito(data.planes[0].pricedebito);
                    setLocalDescription(data.planes[0].description);
                    setLocalMaxConyuge(data.planes[0].maxconyuge);
                    setLocalMaxMenores(data.planes[0].maxmenores);
                    setLocalMaxMayores(data.planes[0].maxmayores);
                    setLocalHeaderColor(data.planes[0].headercolor);
                    setLocalHeaderColorHover(data.planes[0].headercolorhover);
                    setLocalPriceAnual(data.planes[0].priceanual);
                    setLocalMaxEdadTitular(data.planes[0].maxedadtitular);
                    setLocalMaxEdadConyuge(data.planes[0].maxedadconyuge);
                    setLocalMaxEdadMenor(data.planes[0].maxedadmenor);
                    setLocalMaxEdadMayor(data.planes[0].maxedadmayor);
                    setLocalMostrar(data.planes[0].mostrar);
                    setLocalSolCelu(data.planes[0].sol_celu);
                    setLocalValCelu(data.planes[0].val_celu);
                    setLocalSolEmail(data.planes[0].sol_email);
                    setLocalValEmail(data.planes[0].val_email);
                    setLocalPagoOnline(data.planes[0].pago_online);
                    setLocalFchCrea(data.planes[0].fecha_creacion);
                    setLocalFchModi(data.planes[0].fecha_modificacion);
                    setLocalFchBaja(data.planes[0].fecha_baja);
                    setLocalHabilitado(data.planes[0].habilitado);
                    setLocalDadoBaja(data.planes[0].dado_de_baja);
                    setLocalCodigoPlan(data.planes[0].codigoplan);
                    setErrorPlan(null);

                    try {

                        const dataServiciosPlanes = await ObtenerServiciosPlanes('activos', idPlan);                
                        const serviciosTransformados: ServiciosSeleccionar[] = dataServiciosPlanes.servicios_planes.map((item: ServiciosAPI) => ({
                            id: item.idservicio,
                            nombre: item.nombre
                        }));

                        setServiciosPlanes(ordenarServicios(serviciosTransformados));
                        setPlanServicios({id: idPlan, title: data.planes[0].title, servicios: ordenarServicios(serviciosTransformados)});

                    } catch (err) {
                        alert ('Error al obtener los servicios del plan:' + err);
                        setServiciosPlanes([]);}

                } else {
                    
                    // Hubo errores al recuperar el plan
                    setErrorPlan(data.mensaje);
                    setPlan(null);
                    setLocalTitle('');
                    setLocalPrice('');
                    setLocalFchCrea('');
                    setLocalFchModi('');
                    setLocalFchBaja('');
                    setLocalHabilitado('');
                    setLocalDadoBaja('');
                    setLocalCodigoPlan('');
                    setLocalDescription('');
                    setLocalMaxConyuge('');
                    setLocalMaxMenores('');
                    setLocalMaxMayores('');
                    setLocalHeaderColor('');
                    setLocalHeaderColorHover('');
                    setLocalPriceAnual('');
                    setLocalMaxEdadTitular('');
                    setLocalMaxEdadConyuge('');
                    setLocalMaxEdadMenor('');
                    setLocalMaxEdadMayor('');
                    setLocalMostrar('');
                    setLocalSolCelu('');
                    setLocalValCelu('');
                    setLocalSolEmail('');
                    setLocalValEmail('');
                    setLocalPagoOnline('');
                    setLocalFchCrea('');
                    setLocalFchModi('');
                    setLocalFchBaja('');
                    setLocalHabilitado('');
                    setLocalDadoBaja('');
                }
                setIsLoading(false);
                
            } catch (err) {
                setErrorPlan('Error al obtener la información del plan.');
                setIsLoading(false);
                setPlan(null);
                setLocalTitle('');
                setLocalPrice('');
                setLocalFchCrea('');
                setLocalFchModi('');
                setLocalFchBaja('');
                setLocalHabilitado('');
                setLocalDadoBaja('');
                setLocalCodigoPlan('');
                setLocalDescription('');
                setLocalMaxConyuge('');
                setLocalMaxMenores('');
                setLocalMaxMayores('');
                setLocalHeaderColor('');
                setLocalHeaderColorHover('');
                setLocalPriceAnual('');
                setLocalMaxEdadTitular('');
                setLocalMaxEdadConyuge('');
                setLocalMaxEdadMenor('');
                setLocalMaxEdadMayor('');
                setLocalMostrar('');
                setLocalSolCelu('');
                setLocalValCelu('');
                setLocalSolEmail('');
                setLocalValEmail('');
                setLocalPagoOnline('');
                setLocalFchCrea('');
                setLocalFchModi('');
                setLocalFchBaja('');
                setLocalHabilitado('');
                setLocalDadoBaja('');
            }
        };

        if (idPlan > 0) {
            // Estoy editando un plan
            fetchPlan();

        } else {
            // Estoy creando un nuevo plan
            setIsLoading(false);
            setPlan({ id: 0, headercolor: '', headercolorhover: '', title: '', price: '', pricedebito: '', priceanual: '', description: '', maxconyuge: '', maxmenores: '', maxmayores: '', habilitado: 'S', fecha_creacion: '', fecha_modificacion: '', dado_de_baja: 'N', fecha_baja: '', maxedadtitular: '', maxedadconyuge: '', maxedadmenor: '', maxedadmayor: '', codigoplan: '', mostrar: '', sol_celu: '', val_celu: '', sol_email: '', val_email: '', pago_online: '' });
            setLocalTitle('');
            setLocalPrice('');
            setLocalHabilitado('S');
            setLocalDadoBaja('N');
        }

    }, []);

    const handleChangeLocalHabilitado = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalHabilitado(valorMayuscula);
    };

    const handleChangeLocalDadoBaja = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalDadoBaja(valorMayuscula);
    };

    const guardarCambiosLocal = async () => {

        if (idPlan === 0) {
            // Crear nuevo plan

            setLocalFchModi(FechaHoraPostgreSQLTZ());

            const PlanNuevo: PlanEditar = {
                id: 0,
                headercolor: "bg-stone-200",
                headercolorhover: "group-hover:bg-vp-plan-1",
                title: localTitle,
                price: localPrice,
                pricedebito: localPriceDebito,
                priceanual: localPriceAnual,
                description: localDescription,
                maxconyuge: localMaxConyuge,
                maxmenores: localMaxMenores,
                maxmayores: localMaxMayores,
                habilitado: localHabilitado,
                fecha_creacion: localFchCrea,
                fecha_modificacion: localFchModi,
                dado_de_baja: localDadoBaja,
                fecha_baja: localFchBaja,
                maxedadtitular: localMaxEdadTitular,
                maxedadconyuge: localMaxEdadConyuge,
                maxedadmenor: localMaxEdadMenor,
                maxedadmayor: localMaxEdadMayor,
                codigoplan: localCodigoPlan,
                mostrar: localMostrar,
                sol_celu: localSolCelu,
                val_celu: localValCelu,
                sol_email: localSolEmail,
                val_email: localValEmail,
                pago_online: localPagoOnline,
            };
            
            const ServiciosDelPlan: ServiciosSeleccionar[] = planServicios.servicios;

            const respuesta = await InsertarPlan(PlanNuevo);

            if (respuesta.plan_guardado === 'si') {

                // Ahora actualizo los servicios del plan
                const respuestaServicios = await GuardarServiciosPlan(respuesta.id_insertado, ServiciosDelPlan);

                if (respuestaServicios.guardar_servicios_plan_ok === 'si') {
                    guardarCambios(PlanNuevo);
                } else {
                    alert('Error al guardar los servicios del plan: ' + respuestaServicios.mensaje);
                }

            } else {
                alert('Error al guardar los cambios: ' + respuesta.mensaje);
            }
            
        } else {

            if (eliminandoPlan) {
                
                // Se realiza la baja lógica del plan
                // HABILITADO = N
                // DADO_BAJA = S
                const guardarFecha = FechaHoraPostgreSQLTZ();

                // Modificar datos plan
                const PlanEditado: PlanEditar = {
                    id: idPlan,
                    headercolor: localHeaderColor,
                    headercolorhover: localHeaderColorHover,
                    title: localTitle,
                    price: localPrice,
                    pricedebito: localPriceDebito,
                    priceanual: localPriceAnual,
                    description: localDescription,
                    maxconyuge: localMaxConyuge,
                    maxmenores: localMaxMenores,
                    maxmayores: localMaxMayores,
                    habilitado: 'N',
                    fecha_creacion: localFchCrea,
                    fecha_modificacion: guardarFecha,
                    dado_de_baja: 'S',
                    fecha_baja: guardarFecha,
                    maxedadtitular: localMaxEdadTitular,
                    maxedadconyuge: localMaxEdadConyuge,
                    maxedadmenor: localMaxEdadMenor,
                    maxedadmayor: localMaxEdadMayor,
                    codigoplan: localCodigoPlan,
                    mostrar: localMostrar,
                    sol_celu: localSolCelu,
                    val_celu: localValCelu,
                    sol_email: localSolEmail,
                    val_email: localValEmail,
                    pago_online: localPagoOnline,
                };

                const respuesta = await GuardarPlan(PlanEditado);

                if (respuesta.plan_guardado === 'si') {
                    guardarCambios(PlanEditado);
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
                
            } else {
                
                // Modificar plan
                const guardarFecha = FechaHoraPostgreSQLTZ();

                // Modificar datos pais
                const PlanEditado: PlanEditar = {
                    id: idPlan,
                    headercolor: localHeaderColor,
                    headercolorhover: localHeaderColorHover,
                    title: localTitle,
                    price: localPrice,
                    pricedebito: localPriceDebito,
                    priceanual: localPriceAnual,
                    description: localDescription,
                    maxconyuge: localMaxConyuge,
                    maxmenores: localMaxMenores,
                    maxmayores: localMaxMayores,
                    habilitado: localHabilitado,
                    fecha_creacion: localFchCrea,
                    fecha_modificacion: guardarFecha,
                    dado_de_baja: localDadoBaja,
                    fecha_baja: localDadoBaja === 'S' ? guardarFecha : '',
                    maxedadtitular: localMaxEdadTitular,
                    maxedadconyuge: localMaxEdadConyuge,
                    maxedadmenor: localMaxEdadMenor,
                    maxedadmayor: localMaxEdadMayor,
                    codigoplan: localCodigoPlan,
                    mostrar: localMostrar,
                    sol_celu: localSolCelu,
                    val_celu: localValCelu,
                    sol_email: localSolEmail,
                    val_email: localValEmail,
                    pago_online: localPagoOnline,
                };

                const ServiciosDelPlan: ServiciosSeleccionar[] = planServicios.servicios;

                const respuesta = await GuardarPlan(PlanEditado);

                if (respuesta.plan_guardado === 'si') {

                    // Ahora actualizo los servicios del plan
                    const respuestaServicios = await GuardarServiciosPlan(idPlan, ServiciosDelPlan);
                
                    if (respuestaServicios.guardar_servicios_plan_ok === 'si') {
                        guardarCambios(PlanEditado);
                    } else {
                        alert('Error al guardar los servicios del plan: ' + respuestaServicios.mensaje);
                    }

                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
            }
        }
    };

    const handleChangeLocalCodigoPlan = (event: React.ChangeEvent<HTMLInputElement>) => {

        let value = event.target.value;

        // quitar todo lo que no sea letra o número
        value = value.replace(/[^a-zA-Z0-9]/g, '');

        // convertir a mayúsculas
        value = value.toUpperCase();

        setLocalCodigoPlan(value);
    };


    const handleChangeLocalTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalTitle(event.target.value);
    };

    const handleChangeLocalDescription = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalDescription(event.target.value);
    };

    const handleChangeLocalMaxEdadTitular = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d{0,3}$/;
        const value = event.target.value;   
        if (pattern.test(value)) {
            if (value === '' || value === ' ')  {
                setLocalMaxEdadTitular('');
                return;
            }
            if (parseInt(value) <= 150) {
                setLocalMaxEdadTitular(value);
            }
        }
    };

    const handleChangeLocalMaxConyuge = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d{0,1}$/;
        const value = event.target.value;   
        if (pattern.test(value)) {
            if (value === '' || value === ' ')  {
                setLocalMaxConyuge('');
                return;
            }
            if (parseInt(value) <= 1) {
                setLocalMaxConyuge(value);
            }
        }
    };

    const handleChangeLocalMaxEdadConyuge = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d{0,3}$/;
        const value = event.target.value;   
        if (pattern.test(value)) {
            if (value === '' || value === ' ')  {
                setLocalMaxEdadConyuge('');
                return;
            }
            if (parseInt(value) <= 150) {
                setLocalMaxEdadConyuge(value);
            }
        }
    };

    const handleChangeLocalMaxMenores = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d{0,2}$/;
        const value = event.target.value;   
        if (pattern.test(value)) {
            if (value === '' || value === ' ')  {
                setLocalMaxMenores('');
                return;
            }
            if (parseInt(value) <= 99) {
                setLocalMaxMenores(value);
            }
        }
    };

    const handleChangeLocalMaxEdadMenor = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d{0,3}$/;
        const value = event.target.value;   
        if (pattern.test(value)) {
            if (value === '' || value === ' ')  {
                setLocalMaxEdadMenor('');
                return;
            }
            if (parseInt(value) <= 150) {
                setLocalMaxEdadMenor(value);
            }
        }
    };

    const handleChangeLocalMaxMayores = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d{0,2}$/;
        const value = event.target.value;   
        if (pattern.test(value)) {
            if (value === '' || value === ' ')  {
                setLocalMaxMayores('');
                return;
            }
            if (parseInt(value) <= 99) {
                setLocalMaxMayores(value);
            }
        }
    };

    const handleChangeLocalMaxEdadMayor = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d{0,3}$/;
        const value = event.target.value;   
        if (pattern.test(value)) {
            if (value === '' || value === ' ')  {
                setLocalMaxEdadMayor('');
                return;
            }
            if (parseInt(value) <= 150) {
                setLocalMaxEdadMayor(value);
            }
        }
    };

    const handleChangeLocalPrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d*(\.\d{0,2})?$/;
        const value = event.target.value;   
        if (pattern.test(value)) {
            if (value === '' || value === ' ')  {
                setLocalPrice('');
                return;
            }
            setLocalPrice(value);
        }
    };

    const handleChangeLocalPriceDebito = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d*(\.\d{0,2})?$/;
        const value = event.target.value;   
        if (pattern.test(value)) {
            if (value === '' || value === ' ')  {
                setLocalPriceDebito('');
                return;
            }
            setLocalPriceDebito(value);
        }
    };

    const handleChangeLocalPriceAnual = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d*(\.\d{0,2})?$/;
        const value = event.target.value;   
        if (pattern.test(value)) {
            if (value === '' || value === ' ')  {
                setLocalPriceAnual('');
                return;
            }
            setLocalPriceAnual(value);
        }
    };

    const handleChangeLocalMostrar = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalMostrar(valorMayuscula);
    };

    const handleChangeLocalSolCelu = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalSolCelu(valorMayuscula);
    };

    const handleChangeLocalValCelu = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalValCelu(valorMayuscula);
    };

    const handleChangeLocalSolEmail = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalSolEmail(valorMayuscula);
    };

    const handleChangeLocalValEmail = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalValEmail(valorMayuscula);
    };

    const handleChangeLocalPagoOnline = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalPagoOnline(valorMayuscula);
    };

    // Para actualizar los servicios del plan (ante cambios)
    const handleServiciosChange = (newServicio: ServiciosSeleccionar) => {
        
        // Hago una copia de los servicios actuales
        const serviciosCopy = [...planServicios.servicios];

        // Agrego el nuevo servicio
        serviciosCopy.push(newServicio);

        // Ahora ordeno los servicios
        const serviciosOrdenados = ordenarServicios(serviciosCopy);
        
        setPlanServicios({...planServicios, servicios: serviciosOrdenados});
    };
    
    // Para quitar un servicio del plan
    const handleServiciosRemove = (servicio: ServiciosSeleccionar) => {
        // Filtro los servicios para quitar el servicio que se desea eliminar
        const serviciosCopy = planServicios.servicios.filter((item) => item.id !== servicio.id);
        
        // Actualizo el estado de los servicios
        setPlanServicios({...planServicios, servicios: serviciosCopy});
    }

    // Para limpiar todos los servicios del plan
    const handleServiciosClear = () => {
        // Mantengo los datos del plan pero sin servicios
        setPlanServicios({...planServicios, servicios: []});
    }



    return (
        <>
            {isLoading && (
                <div>
                    <p className="text-lg">Por favor espere</p>
                    <CircularProgress />
                </div>
            )}
            {(!isLoading && plan) && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-5">
                    {eliminandoPlan ? (
                        <h1 className="font-metropolis text-xl text-red-700 font-bold mb-2">Eliminando Plan: {plan?.title}</h1>
                    ) : (
                        <h1 className="font-metropolis text-xl font-bold mb-2">{idPlan > 0 ? "Editando Plan: " + plan?.title : "Nuevo Plan"}</h1>
                    )}     
                    <div className="border-t-2 border-b-2 border-vp-azul w-full flex flex-row pt-2 flex-wrap justify-between items-center space-y-2 pb-2">
                        
                        {/* Código del plan */}
                        <div className="flex flex-row justify-start items-center max-w-[160px] flex-grow w-full pt-2">
                            <TextField id="codigo" label="Código del Plan" variant="outlined" value={localCodigoPlan}
                            autoFocus={idPlan === 0 ? true : false}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '16px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '0x',
                                }}
                                onChange={handleChangeLocalCodigoPlan}
                                disabled={eliminandoPlan}
                            />
                        </div>


                        {/* Titulo del plan */}
                        <div className="flex flex-row justify-start items-center max-w-[340px] flex-grow w-full pt-2">
                            <TextField id="title" label="Título del Plan" variant="outlined" value={localTitle}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '16px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '0x',
                                }}
                                onChange={handleChangeLocalTitle}
                                disabled={eliminandoPlan}
                            />
                        </div>

                        {/* Descripción del plan */}
                        <div className="flex flex-row justify-start items-center max-w-[500px] flex-grow w-full pt-2">
                            <TextField id="description" label="Descripción del Plan" variant="outlined" value={localDescription}
                                autoFocus={idPlan !== 0 ? true : false}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '16px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '0x',
                                }}
                                onChange={handleChangeLocalDescription}
                                disabled={eliminandoPlan}
                            />
                        </div>

                        {/* Configuración del titular */}
                        <div className="flex flex-row justify-start items-center max-w-[180px] flex-grow w-full space-x-2 border border-gray-300 p-2 rounded-md ">
                            <Typography variant='caption' sx={{marginBottom: '0px', fontWeight: 'bold'}} className='p-0'>Titular</Typography>
                            <TextField id="maxedadtitular" label="Edad Máx." variant="outlined" value={localMaxEdadTitular}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    maxWidth: '100px',
                                    marginRight: '6px',
                                    marginBottom: '0px',
                                }}
                                inputProps={{
                                    sx: { textAlign: "right",}
                                }}
                                onChange={handleChangeLocalMaxEdadTitular}
                                disabled={eliminandoPlan}
                            />
                        </div>


                        {/* Configuración del cónyuge */}
                        <div className="flex flex-row justify-start items-center max-w-[280px] flex-grow w-full space-x-2 border border-gray-300 p-2 rounded-md ">
                            <Typography variant='caption' sx={{marginBottom: '0px', fontWeight: 'bold'}} className='p-0'>Cónyuge</Typography>
                            <TextField id="maxconyuge" label="Cantidad" variant="outlined" value={localMaxConyuge}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    maxWidth: '80px',
                                    marginRight: '6px',
                                    marginBottom: '0px',
                                }}
                                inputProps={{
                                    style: { textAlign: 'right' },
                                }}
                                onChange={handleChangeLocalMaxConyuge}
                                disabled={eliminandoPlan}
                            />
                            <TextField id="maxedadconyuge" label="Edad Máx." variant="outlined" value={localMaxEdadConyuge}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    maxWidth: '100px',
                                    marginRight: '6px',
                                    marginBottom: '0px',
                                }}
                                inputProps={{
                                    style: { textAlign: 'right' },
                                }}
                                onChange={handleChangeLocalMaxEdadConyuge}
                                disabled={eliminandoPlan}
                            />
                        </div>

                        {/* Configuración de menores */}
                        <div className="flex flex-row justify-start items-center max-w-[280px] flex-grow w-full space-x-2 border border-gray-300 p-2 rounded-md ">
                            <Typography variant='caption' sx={{marginBottom: '0px', fontWeight: 'bold'}} className='p-0'>Menores</Typography>
                            <TextField id="maxmenores" label="Cantidad" variant="outlined" value={localMaxMenores}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    maxWidth: '80px',
                                    marginRight: '6px',
                                    marginBottom: '0px',
                                }}
                                inputProps={{
                                    style: { textAlign: 'right' },
                                }}
                                onChange={handleChangeLocalMaxMenores}
                                disabled={eliminandoPlan}
                            />
                            <TextField id="maxedadmenores" label="Edad Máx." variant="outlined" value={localMaxEdadMenor}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    maxWidth: '110px',
                                    marginRight: '6px',
                                    marginBottom: '0px',
                                }}
                                inputProps={{
                                    style: { textAlign: 'right' },
                                }}
                                onChange={handleChangeLocalMaxEdadMenor}
                                disabled={eliminandoPlan}
                            />
                        </div>

                        {/* Configuración de mayores */}
                        <div className="flex flex-row justify-start items-center max-w-[280px] flex-grow w-full space-x-2 border border-gray-300 p-2 rounded-md ">
                            <Typography variant='caption' sx={{marginBottom: '0px', fontWeight: 'bold'}} className='p-0'>Mayores</Typography>
                            <TextField id="maxmayores" label="Cantidad" variant="outlined" value={localMaxMayores}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    maxWidth: '80px',
                                    marginRight: '6px',
                                    marginBottom: '0px',
                                }}
                                inputProps={{
                                    style: { textAlign: 'right' },
                                }}
                                onChange={handleChangeLocalMaxMayores}
                                disabled={eliminandoPlan}
                            />
                            <TextField id="maxedadmayores" label="Edad Máx." variant="outlined" value={localMaxEdadMayor}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    maxWidth: '110px',
                                    marginRight: '6px',
                                    marginBottom: '0px',
                                }}
                                inputProps={{
                                    style: { textAlign: 'right' },
                                }}
                                onChange={handleChangeLocalMaxEdadMayor}
                                disabled={eliminandoPlan}
                            />
                        </div>

                        {/* Configuración del importe */}
                        <div className="flex flex-row justify-start items-center max-w-[440px] flex-grow w-full space-x-2 border border-gray-300 p-2 rounded-md ">
                            <Typography variant='caption' sx={{marginBottom: '0px', fontWeight: 'bold'}} className='p-0'>Importes</Typography>
                            <TextField id="precio" label="Precio Lista" variant="outlined" value={localPrice}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    maxWidth: '120px',
                                    marginRight: '6px',
                                    marginBottom: '0px',
                                }}
                                inputProps={{
                                    style: { textAlign: 'right' },
                                }}
                                onChange={handleChangeLocalPrice}
                                disabled={eliminandoPlan}
                            />
                            <TextField id="preciodebito" label="Precio Débito" variant="outlined" value={localPriceDebito}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    maxWidth: '120px',
                                    marginRight: '6px',
                                    marginBottom: '0px',
                                }}
                                inputProps={{
                                    style: { textAlign: 'right' },
                                }}
                                onChange={handleChangeLocalPriceDebito}
                                disabled={eliminandoPlan}
                            />
                            <TextField id="precioanual" label="Precio Anual" variant="outlined" value={localPriceAnual}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    maxWidth: '120px',
                                    marginRight: '6px',
                                    marginBottom: '0px',
                                }}
                                inputProps={{
                                    style: { textAlign: 'right' },
                                }}
                                onChange={handleChangeLocalPriceAnual}
                                disabled={eliminandoPlan}
                            />
                        </div>

                        {/* Configuración de celular */}
                        <div className="flex flex-row justify-start items-center max-w-[300px] flex-grow w-full space-x-2 border border-gray-300 p-2 rounded-md ">
                            <Typography variant='caption' sx={{marginBottom: '0px', fontWeight: 'bold'}} className='p-0'>Celular</Typography>
                            <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                                <FormControl fullWidth sx={{marginRight: '6px',}}>
                                    <InputLabel id="label-solcelu"
                                        sx={{top: '-6px',
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, 0px) scale(0.75)',
                                            },
                                        }}
                                        >
                                            Solicitar
                                        </InputLabel>
                                    <Select
                                        labelId="label-solcelu"
                                        id="solcelu"
                                        value={localSolCelu}
                                        label="Solicitar"
                                        sx={{
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px',
                                            },
                                            width: '100%',
                                            marginRight: '6px',
                                            marginBottom: '0px',
                                        }}
                                        onChange={handleChangeLocalSolCelu}
                                        disabled={eliminandoPlan}
                                        >
                                        <MenuItem value={'S'}>Si</MenuItem>
                                        <MenuItem value={'N'}>No</MenuItem>
                                    </Select>
                                </FormControl> 
                            </div>
                            <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                                <FormControl fullWidth sx={{marginRight: '6px',}}>
                                    <InputLabel id="label-valcelu"
                                        sx={{top: '-6px',
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, 0px) scale(0.75)',
                                            },
                                        }}
                                        >
                                            Validar
                                        </InputLabel>
                                    <Select
                                        labelId="label-valcelu"
                                        id="valcelu"
                                        value={localValCelu}
                                        label="Validar"
                                        sx={{
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px',
                                            },
                                            width: '100%',
                                            marginRight: '6px',
                                            marginBottom: '0px',
                                        }}
                                        onChange={handleChangeLocalValCelu}
                                        disabled={eliminandoPlan}
                                        >
                                        <MenuItem value={'S'}>Si</MenuItem>
                                        <MenuItem value={'N'}>No</MenuItem>
                                    </Select>
                                </FormControl> 
                            </div>
                        </div>

                        {/* Configuración de email */}
                        <div className="flex flex-row justify-start items-center max-w-[300px] flex-grow w-full space-x-2 border border-gray-300 p-2 rounded-md ">
                            <Typography variant='caption' sx={{marginBottom: '0px', fontWeight: 'bold'}} className='p-0'>Email</Typography>
                            <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                                <FormControl fullWidth sx={{marginRight: '6px',}}>
                                    <InputLabel id="label-solemail"
                                        sx={{top: '-6px',
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, 0px) scale(0.75)',
                                            },
                                        }}
                                        >
                                            Solicitar
                                        </InputLabel>
                                    <Select
                                        labelId="label-solemail"
                                        id="solemail"
                                        value={localSolEmail}
                                        label="Solicitar"
                                        sx={{
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px',
                                            },
                                            width: '100%',
                                            marginRight: '6px',
                                            marginBottom: '0px',
                                        }}
                                        onChange={handleChangeLocalSolEmail}
                                        disabled={eliminandoPlan}
                                        >
                                        <MenuItem value={'S'}>Si</MenuItem>
                                        <MenuItem value={'N'}>No</MenuItem>
                                    </Select>
                                </FormControl> 
                            </div>
                            <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                                <FormControl fullWidth sx={{marginRight: '6px',}}>
                                    <InputLabel id="label-valemail"
                                        sx={{top: '-6px',
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, 0px) scale(0.75)',
                                            },
                                        }}
                                        >
                                            Validar
                                        </InputLabel>
                                    <Select
                                        labelId="label-valemail"
                                        id="valemail"
                                        value={localValEmail}
                                        label="Validar"
                                        sx={{
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px',
                                            },
                                            width: '100%',
                                            marginRight: '6px',
                                            marginBottom: '0px',
                                        }}
                                        onChange={handleChangeLocalValEmail}
                                        disabled={eliminandoPlan}
                                        >
                                        <MenuItem value={'S'}>Si</MenuItem>
                                        <MenuItem value={'N'}>No</MenuItem>
                                    </Select>
                                </FormControl> 
                            </div>
                        </div>

                        {/* Configuración de modo de pago */}
                        <div className="flex flex-row justify-start items-center max-w-[240px] flex-grow w-full space-x-2 border border-gray-300 p-2 rounded-md ">
                            <Typography variant='caption' sx={{marginBottom: '0px', fontWeight: 'bold'}} className='p-0'>Forma de</Typography>
                            <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                                <FormControl fullWidth sx={{marginRight: '6px',}}>
                                    <InputLabel id="label-online"
                                        sx={{top: '-6px',
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, 0px) scale(0.75)',
                                            },
                                        }}
                                        >
                                            Pago
                                        </InputLabel>
                                    <Select
                                        labelId="label-online"
                                        id="online"
                                        value={localPagoOnline}
                                        label="Online"
                                        sx={{
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px',
                                            },
                                            width: '100%',
                                            marginRight: '6px',
                                            marginBottom: '0px',
                                        }}
                                        onChange={handleChangeLocalPagoOnline}
                                        disabled={eliminandoPlan}
                                        >
                                        <MenuItem value={'S'}>Online</MenuItem>
                                        <MenuItem value={'N'}>Manual</MenuItem>
                                        <MenuItem value={'P'}>Preguntar</MenuItem>
                                    </Select>
                                </FormControl> 
                            </div>
                        </div>

                        {/* Mostrar en lista de planes */}
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-mostrar"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Mostrar
                                    </InputLabel>
                                <Select
                                    labelId="label-mostrar"
                                    id="mostrar"
                                    value={localMostrar}
                                    label="Mostrar"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '16px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '0px',
                                    }}
                                    onChange={handleChangeLocalMostrar}
                                    disabled={eliminandoPlan}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                    <MenuItem value={'W'}>Web</MenuItem>
                                    <MenuItem value={'C'}>Cotizador</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {/* Habilitado */}
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-habilitado"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Habilitado
                                    </InputLabel>
                                <Select
                                    labelId="label-habilitado"
                                    id="habilitado"
                                    value={localHabilitado}
                                    label="Habilitado"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '16px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '0px',
                                    }}
                                    onChange={handleChangeLocalHabilitado}
                                    disabled={eliminandoPlan}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {/* Dado de baja */}
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-dadobaja"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Baja
                                    </InputLabel>
                                <Select
                                    labelId="label-dadobaja"
                                    id="dadobaja"
                                    value={localDadoBaja}
                                    label="Baja"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '16px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '0px',
                                    }}
                                    onChange={handleChangeLocalDadoBaja}
                                    disabled={eliminandoPlan}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {/* Fecha de creación */}
                        {plan.id > 0 && (
                            <div className="flex flex-col justify-between items-center max-w-[190px] flex-grow w-full">
                                <TextField id="fchcrea" label="Fec. Creado" variant="outlined" value={localFchCrea}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '16px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '0px',
                                    }}
                                    disabled
                                />
                            </div>)
                        }
                        {/* Servicios del plan */}
                        <div className="flex flex-col justify-between items-center flex-grow w-full pb-4">
                            <SeleccionarServicios elPlan={planServicios} onChange={handleServiciosChange} onRemove={handleServiciosRemove} onClear={handleServiciosClear} eliminando={eliminandoPlan}/>   
                        </div>
                    </div>
                    <div className="flex flex-row justify-end items-center space-x-2">
                        {eliminandoPlan ? (
                            <>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={guardarCambiosLocal} ><DeleteForeverIcon /> Eliminar</button>
                                <button className="mt-3 vp-btn-azul-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        ) : (
                            <>
                                <button className="mt-3 vp-btn-azul-sm" onClick={guardarCambiosLocal} ><SaveIcon /> Guardar Cambios</button>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        )}
                        
                    </div>
                </div>
            )}
        </>
    );
};

// MuiInputLabel-formControl MuiInputLabel-animated MuiInputLabel-shrink MuiInputLabel-sizeMedium MuiInputLabel-outlined css-xo08j0-MuiFormLabel-root-MuiInputLabel-root